import { ReportType } from '@constants'
import theme from '@styles/theme'
import { openLinkInTab, saveBlobAsFile } from '@utils'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'
import { network } from './network'

type GenerateReportArgs = {
  business_id: number | string
  type: ReportType
  columns: ReportColumn[]
  extend_accounts?: boolean
  append_comparison_columns?: boolean
  comparison_mode?: 'amount' | 'percent'
  tag_ids?: number[]
}

type ReportColumnDateAt = {
  name?: string
  date_at: string
}

type ReportColumnDateRange = {
  name?: string
  date_from: string
  date_to: string
}

export type ReportColumn = ReportColumnDateRange | ReportColumnDateAt

const toastReportPromise = (promise: Promise<void>): Promise<void> => {
  toast.promise(
    promise,
    {
      loading: <Trans i18nKey="reports.toastLoading" />,
      success: <Trans i18nKey="reports.toastSuccess" />,
      error: <Trans i18nKey="reports.toastError" />
    },
    {
      style: {
        borderRadius: `${theme.borderRadius.md}rem`,
        background: theme.colors.menuBg,
        color: theme.colors.neutralWhite
      }
    }
  )

  return promise
}

const fetchReport = (url: string, data: GenerateReportArgs) => {
  const promise = network.httpClient
    .post(
      url,
      data,
      { timeout: 120000 } // Two mins
    )
    .then(({ data }) => {
      openLinkInTab(data.report_url)
    })

  return toastReportPromise(promise)
}

export const generateReport = (data: GenerateReportArgs): Promise<void> => {
  const reportUrl = `/v1/report/${data.business_id}/download`

  return fetchReport(reportUrl, data)
}

export const downloadCsvReport = async (data: GenerateReportArgs): Promise<void> => {
  const reportUrl = `/v1/report/business/${data.business_id}/report/csv/`

  const promise = network.httpClient
    .post(
      reportUrl,
      data,
      { timeout: 120000, responseType: 'blob' } // Two mins
    )
    .then(response => {
      const filename =
        response.headers?.['content-disposition']?.split('filename=')[1]?.slice(1, -1) ??
        `${data.business_id}_${data.type}.csv`

      saveBlobAsFile(response.data, filename)
    })

  return toastReportPromise(promise)
}

export const downloadDocxReport = async (data: GenerateReportArgs): Promise<void> => {
  const reportUrl = `/v1/report/business/${data.business_id}/report/docx/`

  return fetchReport(reportUrl, { ...data })
}
